import React from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { V2Button } from '@uhg-abyss/web/ui/Button';
import { V2Badge } from '@uhg-abyss/web/ui/Badge';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';

const SectionStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

const SectionHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '24px',
});

const Label = styled('p', {
  margin: 0,
  fontWeight: 700,
});

const Value = styled('p', {
  margin: 0,
  wordBreak: 'break-all',
  whiteSpace: 'normal',
});

const DetailGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});

const TwoColumnRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(240px, 340px))',
  gap: '10px',
  justifyContent: 'start',
});

const getJobTitleDisplay = (value) => {
  const jobTitles = {
    administrativeStaff: 'Administrative staff',
    supportStaff: 'Support staff',
    clinician: 'Clinician',
    physician: 'Physician',
  };
  return jobTitles[value] || value || '';
};

const formatPhoneDisplay = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length !== 10) return value || '';
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const SubmitterInformationCard = ({ onEdit, isPrinting }) => {
  const abyssForm = useFormV2();

  const submitterInfo = abyssForm.getValues('submitterInformation') || {};
  const fullName = [submitterInfo.firstName, submitterInfo.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const jobTitle = getJobTitleDisplay(submitterInfo.jobTitle);
  const phone = formatPhoneDisplay(submitterInfo.phoneNumber || '');
  const claimDate = submitterInfo.claimDate
    ? dayjs(submitterInfo.claimDate).format('MM/DD/YYYY')
    : '';

  return (
    <V2Card>
      <SectionHeader>
        <h4>Submitter information</h4>
        {!isPrinting && (
          <V2Button
            variant="outline"
            size="$sm"
            onClick={() => onEdit(0)}
            icon={{
              icon: 'edit',
              position: 'leading',
              variant: 'outlined',
            }}
          >
            Edit
          </V2Button>
        )}
      </SectionHeader>
      <SectionStack>
        <TwoColumnRow>
          <DetailGroup>
            <Label>Full name</Label>
            <Value>{fullName}</Value>
          </DetailGroup>
          <DetailGroup>
            <Label>Job title</Label>
            <Value>{jobTitle}</Value>
          </DetailGroup>
        </TwoColumnRow>

        <TwoColumnRow>
          <DetailGroup>
            <Label>Email address</Label>
            <Value>{submitterInfo.emailAddress}</Value>
            {submitterInfo.preferredModeOfContact === 'Email' && (
              <V2Badge variant="info">Preferred contact</V2Badge>
            )}
          </DetailGroup>
          <DetailGroup>
            <Label>Phone number</Label>
            <Value>{phone}</Value>
            {submitterInfo.preferredModeOfContact === 'Phone' && (
              <V2Badge variant="info">Preferred contact</V2Badge>
            )}
          </DetailGroup>
        </TwoColumnRow>

        <TwoColumnRow>
          <DetailGroup>
            <Label>Have you submitted a claim?</Label>
            <Value>{submitterInfo.submittedClaim}</Value>
          </DetailGroup>
          {submitterInfo.submittedClaim === 'Yes' && (
            <DetailGroup>
              <Label>Claim submission date</Label>
              <Value>{claimDate}</Value>
            </DetailGroup>
          )}
        </TwoColumnRow>
      </SectionStack>
    </V2Card>
  );
};
